import { NavigateNext, MoreHoriz } from '@mui/icons-material';
import { Breadcrumbs, Typography, Popover, MenuItem } from '@mui/material';
import useMyMenuItems from 'hooks/useMyMenuItems';
import { Link } from 'react-router-dom';
import { useState, useRef } from 'react';

export type HierarchyBreadcrumbLevel = { id: string; name: string; recordTypeId: number; level: number; recordTypeName: string };

export type HierarchyBreadcrumbProps = {
    recordTypeName: string;
    levels: HierarchyBreadcrumbLevel[];
};

export const HierarchyBreadcrumb = ({ recordTypeName, levels }: HierarchyBreadcrumbProps) => {
    const { getMenuItemByRecordTypeId } = useMyMenuItems();
    const [showHiddenLevels, setShowHiddenLevels] = useState(false);
    const dotsRef = useRef<HTMLDivElement>(null);

    const getRecordTypeRoute = (recordTypeId: number) => {
        if (!getMenuItemByRecordTypeId) return '';
        const menuItem = getMenuItemByRecordTypeId(recordTypeId);
        return menuItem || '';
    };

    const handleDotsClick = () => {
        setShowHiddenLevels(true);
    };

    const handleClose = () => {
        setShowHiddenLevels(false);
    };

    const handleOptionChange = (levelId: string) => {
        const level = levels.find((l) => l.id === levelId);
        if (level) {
            window.location.href = `./?recordId=${level.id}&recordType=${level.recordTypeId}`;
        }
        handleClose();
    };

    // Obtener niveles ocultos (todos excepto el primero y el último)
    const hiddenLevels = levels.slice(1, -1);

    if (levels.length === 0) {
        return null;
    }

    const shouldShowDots = levels.length > 3;

    return (
        <>
            <Breadcrumbs separator={<NavigateNext />} aria-label="breadcrumb" sx={{ width: 'calc(100% - 280px)' }}>
                {/* Primer elemento: nombre del record type del parent level 0 */}
                {levels.length > 0 && (
                    <Typography
                        component={Link}
                        to={getRecordTypeRoute(levels[0].recordTypeId)}
                        fontSize="20px"
                        fontWeight={300}
                        sx={({ palette }) => ({
                            color: palette.primary[400],
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        })}
                    >
                        {levels[0].recordTypeName}
                    </Typography>
                )}

                {/* Primer level: con href para navegación */}
                {levels.length > 0 && (
                    <Typography
                        key={`first-level-${levels[0].id}`}
                        fontSize="20px"
                        component="a"
                        href={`./?recordId=${levels[0].id}&recordType=${levels[0].recordTypeId}`}
                        rel="noreferrer"
                        fontWeight={300}
                        sx={({ palette }) => ({
                            textDecoration: 'none',
                            color: palette.primary[400],
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        })}
                    >
                        {levels[0].name}
                    </Typography>
                )}

                {/* Puntos suspensivos cuando hay más de 3 niveles */}
                {shouldShowDots && (
                    <Typography
                        ref={dotsRef}
                        onClick={handleDotsClick}
                        fontSize="20px"
                        fontWeight={300}
                        sx={{
                            color: '#666',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#333'
                            }
                        }}
                    >
                        <MoreHoriz />
                    </Typography>
                )}

                {/* Niveles intermedios cuando no se necesitan puntos suspensivos */}
                {!shouldShowDots &&
                    levels.length > 2 &&
                    levels.slice(1, -1).map((level, index) => (
                        <Typography
                            key={`intermediate-${level.id}`}
                            fontSize="20px"
                            component="a"
                            href={`./?recordId=${level.id}&recordType=${level.recordTypeId}`}
                            rel="noreferrer"
                            fontWeight={300}
                            sx={({ palette }) => ({
                                textDecoration: 'none',
                                color: palette.primary[400],
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            })}
                        >
                            {level.name}
                        </Typography>
                    ))}

                {/* Último elemento: solo Typography sin link */}
                {levels.length > 1 && (
                    <Typography
                        fontSize="20px"
                        fontWeight={500}
                        sx={({ palette }) => ({
                            color: palette.secondary.main
                        })}
                    >
                        {levels[levels.length - 1].name}
                    </Typography>
                )}
            </Breadcrumbs>

            {/* Popover para mostrar niveles ocultos */}
            <Popover
                anchorEl={dotsRef.current}
                open={showHiddenLevels}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        mt: '5px',
                        minWidth: '200px',
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }
                }}
            >
                {hiddenLevels.length ? (
                    hiddenLevels.map((option) => (
                        <MenuItem
                            sx={{
                                border: 'solid 1px #D9D9D9',
                                height: '40px',
                                px: '12px',
                                bgcolor: '#F5F6F7',
                                minWidth: '200px'
                            }}
                            key={option.id}
                            onClick={() => handleOptionChange(option.id)}
                        >
                            <Typography fontSize="14px" sx={{ overflow: 'hidden', width: '100%', textOverflow: 'ellipsis' }}>
                                {option.name}
                            </Typography>
                        </MenuItem>
                    ))
                ) : (
                    <Typography fontSize="14px" sx={{ py: 2, px: 1 }} color="#C6C4C4" align="center">
                        No hay opciones disponibles
                    </Typography>
                )}
            </Popover>
        </>
    );
};
